// src/components/auth/ResetPassword.js
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WelcomeHeader from '../layout/WelcomeHeader';
import styles from './Login.module.css';
import { authApi } from '../../api';

function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'done'
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!token) {
      setErrorMessage(t('resetPassword.noToken'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage(t('resetPassword.mismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setErrorMessage(t('resetPassword.tooShort'));
      return;
    }

    setStatus('sending');
    const result = await authApi.resetPassword(token, newPassword);

    if (result.success) {
      setStatus('done');
    } else {
      setStatus(null);
      const code = result.error?.code;
      if (code === 'INVALID_TOKEN') {
        setErrorMessage(t('resetPassword.invalidToken'));
      } else if (code === 'VALIDATION_ERROR') {
        setErrorMessage(t('resetPassword.requirements'));
      } else {
        setErrorMessage(result.error?.message || t('resetPassword.resetError'));
      }
    }
  };

  return (
    <>
      <WelcomeHeader />
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.authLogo}>
            <img src={require('../../assets/logo_1.png')} alt="MangaShelf" className={styles.authLogoImg} />
          </div>

          <div className={styles.authHeader}>
            <h1 className={styles.authTitle}>{t('resetPassword.title')}</h1>
            <p className={styles.authSubtitle}>{t('resetPassword.subtitle')}</p>
          </div>

          {status === 'done' ? (
            <div className={styles.authActions}>
              <p className={styles.authSubtitle}>
                {t('resetPassword.doneText')}
              </p>
              <button onClick={() => navigate('/login')} className={styles.submitButton}>
                {t('resetPassword.goToLogin')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.authForm}>
              {!token && (
                <div className={styles.errorMessage}>
                  {t('resetPassword.noTokenInUrl')}
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('resetPassword.newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className={styles.formInput}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  autoComplete="new-password"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('resetPassword.confirmPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={styles.formInput}
                  placeholder={t('resetPassword.repeatPassword')}
                  autoComplete="new-password"
                />
              </div>

              {errorMessage && (
                <div className={styles.errorMessage}>{errorMessage}</div>
              )}

              <button type="submit" className={styles.submitButton} disabled={status === 'sending' || !token}>
                {status === 'sending' ? t('resetPassword.saving') : t('resetPassword.changePassword')}
              </button>
            </form>
          )}

          <div className={styles.divider}>
            <span>{t('auth.or')}</span>
          </div>

          <div className={styles.authActions}>
            <button onClick={() => navigate('/forgot-password')} className={styles.linkButton}>
              {t('resetPassword.requestNewLink')}
            </button>
            <button onClick={() => navigate('/login')} className={styles.backButton}>
              ← {t('forgotPassword.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ResetPassword;
